import { useState } from "react";
import UserItem from "./UserItem";
import { IPageLink, TAdminUser } from "../../../../models/superadmin";
import Chevron from "../../../UI/Icons/Chevron";
import PaginationControls from "../../../Pagination/PaginationControls";

// Define the props for the UserSection component
interface UserSectionProps {
  title: string; // Title of the section (e.g., 'Super Admins' or 'General Admins')
  type: "superAdmin" | "generalAdmin"; // Type of the user being displayed
  data: TAdminUser[]; // List of admin users to display
  total: number; // Total number of users
  searchInput: string; // The search query to highlight matching text
  currentPage: number; // The current page for pagination
  totalPages: number; // The total number of pages for pagination
  paginationLinks: IPageLink[]; // Pagination links for navigation
  setPerPageList: (value: number) => void; // Function to set the number of items per page
  setCurrentPage: (value: number) => void; // Function to set the current page
  onPageChange: (url: string | null) => void; // Function to handle page changes
  handleEdit: (id: number | null, type: string) => void; // Function to handle the edit action

  getAllUsers: () => Promise<void>;
}

export default function UserSection({
  title,
  type,
  data,
  total,
  searchInput,
  currentPage,
  totalPages,
  paginationLinks,
  setPerPageList,
  setCurrentPage,
  onPageChange,
  handleEdit,
  getAllUsers
}: UserSectionProps) {
  const [isOpen, setIsOpen] = useState(true); // State to track whether the section is open or closed
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null); // State to track the currently open dropdown for actions

  return (
    <div className="d-flex flex-column gap-10">
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="bg-teal-light py-8px px-15px radius-8px d-flex gap-8 align-items-center cursor-pointer"
      >
        <Chevron isOpen={isOpen} />{" "}
        <div className="fs-16 fw-700 text-black">
          {total > 0 ? total : 0} {title}{" "}
        </div>
      </div>

      {isOpen && (
        <div className="d-flex flex-column">
          {data?.length > 0 ? (
            data.map((admin) => (
              <UserItem
                key={admin.id}
                admin={admin}
                searchInput={searchInput}
                isOpen={openDropdownId === admin.id} // Check if the dropdown for this user is open
                onToggle={() =>
                  setOpenDropdownId((prev) =>
                    prev === admin.id ? null : admin.id
                  )
                }
                onEdit={() => handleEdit(admin.id, type)}
                total={total}
                getAllUsers={getAllUsers}
              />
            ))
          ) : (
            <div className="d-flex flex-column align-items-center text-grey-medium">
              No {title} found
            </div>
          )}

          {/* Render pagination controls if there are more than 10 users */}
          {total > 10 && (
            <PaginationControls
              currentPage={currentPage} // Current page number
              totalPages={totalPages} // Total number of pages
              paginationLinks={paginationLinks} // List of pagination links
              onPageChange={onPageChange} // Function to handle page change
              setPerPageList={setPerPageList} // Function to set the number of items per page
              setCurrentPage={setCurrentPage} // Function to set the current page
            />
          )}
        </div>
      )}
    </div>
  );
}
